import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Snackbar,
  TablePagination,
  Switch,
  FormControlLabel,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PlayArrow as TestIcon,
  ContentCopy as CopyIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon,
  ListAlt as LogsIcon,
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Chat as WhatsAppIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  AutoAwesome as AiIcon,
  Replay as RetryIcon,
  Visibility as ViewIcon,
  StarBorder as StarBorderIcon,
  Shield as ShieldIcon,
  HelpOutline as HelpIcon,
  NotificationsActive as TriggerIcon,
  Assignment as TemplateIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import api from '../services/api'
import type { IntegrationConfig, ServiceType } from '../types/integration'
import IntegrationConfigForm from '../components/integrations/IntegrationConfigForm'

// WhatsApp Sub-Components
import WhatsAppNotificationSettings from '../components/whatsapp/WhatsAppNotificationSettings'
import WhatsAppTemplatesTab from '../components/whatsapp/WhatsAppTemplatesTab'
import WhatsAppTestMessage from '../components/whatsapp/WhatsAppTestMessage'

// Email Sub-Components
import EmailNotificationSettingsTab from '../components/email/EmailNotificationSettingsTab'
import EmailTemplatesTab from '../components/email/EmailTemplatesTab'
import EmailTestMessageTab from '../components/email/EmailTestMessageTab'

interface WebhookEventItem {
  id: string
  provider_name: string
  event_type: string
  event_id?: string
  processing_status: string
  processing_error?: string
  signature_verified: boolean
  order_id?: string
  created_at: string
  processed_at?: string
  raw_payload?: string
  parsed_payload?: any
  headers?: Record<string, string>
}

interface WebhookEndpointInfo {
  id: string
  title: string
  provider: string
  serviceType: ServiceType
  icon: React.ReactNode
  color: string
  method: string
  endpointPath: string
  fullUrl: string
  authType: string
  description: string
  events: string[]
  instructions: string[]
}

const API_BASE_URL = window.location.origin.includes('localhost')
  ? 'https://api.dhanshika.in'
  : `${window.location.protocol}//api.${window.location.hostname.replace(/^admin\./, '')}`

const TAB_KEYS = ['payment', 'logistics', 'whatsapp', 'sms', 'email', 'ai', 'webhooks', 'logs']

const SERVICE_META: Record<
  string,
  {
    title: string
    subtitle: string
    icon: React.ReactNode
    color: string
    bgColor: string
    description: string
  }
> = {
  payment: {
    title: 'Payment Gateways',
    subtitle: 'Manage online checkouts, UPI, Cards, NetBanking, and instant payment settlement gateways.',
    icon: <PaymentIcon sx={{ fontSize: 28, color: '#0C2340' }} />,
    color: '#0C2340',
    bgColor: 'rgba(12, 35, 64, 0.06)',
    description: 'Razorpay is configured as your primary B2B payment gateway. Webhooks automatically capture customer payments and reconcile invoices.',
  },
  logistics: {
    title: 'Logistics & Shipping Carriers',
    subtitle: 'Configure automated courier dispatch, AWB generation, and tracking with multi-carrier engines.',
    icon: <ShippingIcon sx={{ fontSize: 28, color: '#0284C7' }} />,
    color: '#0284C7',
    bgColor: 'rgba(2, 132, 199, 0.06)',
    description: 'Connect Delhivery Express and Shiprocket for automated parcel dispatch, live tracking webhooks, and reverse logistics (RTO).',
  },
  whatsapp: {
    title: 'WhatsApp Business & Messaging',
    subtitle: 'Enterprise WhatsApp Cloud API for automated order alerts, dispatch updates, templates, and customer broadcasts.',
    icon: <WhatsAppIcon sx={{ fontSize: 28, color: '#16A34A' }} />,
    color: '#16A34A',
    bgColor: 'rgba(22, 163, 74, 0.06)',
    description: 'eNotify WhatsApp Cloud API powers real-time order confirmation receipts, template validation, tracking links, and test sends.',
  },
  sms: {
    title: 'SMS & OTP DLT Gateways',
    subtitle: 'High-priority transactional DLT gateways for customer login OTPs and critical alerts.',
    icon: <SmsIcon sx={{ fontSize: 28, color: '#D97706' }} />,
    color: '#D97706',
    bgColor: 'rgba(217, 119, 6, 0.06)',
    description: 'TrueBulkSMS gateway handles DLT approved SMS OTPs, order notifications, and fallback message delivery when WhatsApp is unreachable.',
  },
  email: {
    title: 'Transactional Email Services',
    subtitle: 'Deliver GST invoices, quotation PDFs, account statements, and password resets directly to inboxes.',
    icon: <EmailIcon sx={{ fontSize: 28, color: '#2563EB' }} />,
    color: '#2563EB',
    bgColor: 'rgba(37, 99, 235, 0.06)',
    description: 'SendGrid, ZeptoMail, and SMTP provide high-deliverability transactional email pipelines with open/click tracking and templates.',
  },
  ai: {
    title: 'AI & Language Intelligence',
    subtitle: 'Voice search, automatic B2B product catalog enrichment, MOQ recommendations, and translation.',
    icon: <AiIcon sx={{ fontSize: 28, color: '#7C3AED' }} />,
    color: '#7C3AED',
    bgColor: 'rgba(124, 58, 237, 0.06)',
    description: 'Sarvam AI (sarvam-105b) generates rich multilingual B2B catalog descriptions, HSN/GST classification, and executive customer insights.',
  },
}

const WEBHOOK_ENDPOINTS: WebhookEndpointInfo[] = [
  {
    id: 'razorpay',
    title: 'Razorpay Payments Webhook',
    provider: 'Razorpay',
    serviceType: 'payment',
    icon: <PaymentIcon sx={{ color: '#0C2340', fontSize: 28 }} />,
    color: '#0C2340',
    method: 'POST',
    endpointPath: '/api/admin/razorpay/webhooks',
    fullUrl: `${API_BASE_URL}/api/admin/razorpay/webhooks`,
    authType: 'HMAC SHA256 Signature (X-Razorpay-Signature)',
    description: 'Receives instant payment captures, authorized charges, failures, and refunds to automatically mark orders as paid and generate invoices.',
    events: [
      'payment.captured',
      'payment.failed',
      'payment.authorized',
      'payment_link.paid',
      'payment_link.expired',
      'refund.processed',
    ],
    instructions: [
      'Log into Razorpay Dashboard > Settings > Webhooks > Add New Webhook.',
      `Paste the Webhook URL: ${API_BASE_URL}/api/admin/razorpay/webhooks`,
      'Set Secret: Enter the same secret configured in Admin Portal > Payment Settings.',
      'Select active events: payment.captured, payment.failed, payment_link.paid, refund.processed.',
      'Click Save Webhook.',
    ],
  },
  {
    id: 'delhivery',
    title: 'Delhivery Logistics Webhook',
    provider: 'Delhivery',
    serviceType: 'logistics',
    icon: <ShippingIcon sx={{ color: '#E53E3E', fontSize: 28 }} />,
    color: '#E53E3E',
    method: 'POST',
    endpointPath: '/api/webhooks/delhivery',
    fullUrl: `${API_BASE_URL}/api/webhooks/delhivery`,
    authType: 'IP Allow-list Verification (DELHIVERY_WEBHOOK_ALLOWED_IPS)',
    description: 'Receives live courier dispatch, transit, out-for-delivery, and delivery scans to sync order shipment status in real time.',
    events: [
      'Delivered (DL)',
      'In Transit (IT)',
      'Out for Delivery (OFD)',
      'Dispatched (UD)',
      'Return to Origin (RTO)',
    ],
    instructions: [
      'Log into Delhivery Client Portal (B2B/B2C).',
      `Set Push Webhook URL: ${API_BASE_URL}/api/webhooks/delhivery`,
      'Ensure your server IP is registered in Delhivery client panel allow-list.',
      'Enable status update triggers.',
    ],
  },
  {
    id: 'shiprocket',
    title: 'Shiprocket / Shiprite Webhook',
    provider: 'Shiprocket',
    serviceType: 'logistics',
    icon: <ShippingIcon sx={{ color: '#7C3AED', fontSize: 28 }} />,
    color: '#7C3AED',
    method: 'POST',
    endpointPath: '/api/webhooks/shiprocket',
    fullUrl: `${API_BASE_URL}/api/webhooks/shiprocket`,
    authType: 'Token Header Verification (x-api-key / x-shiprocket-token)',
    description: 'Receives AWB assignments, courier pickups, live tracking milestones, and automated WhatsApp alert triggers for multi-courier shipments.',
    events: [
      'AWB Assigned',
      'Pickup Scheduled / Picked Up',
      'In Transit',
      'Out for Delivery',
      'Delivered',
      'RTO / NDR Initiated',
    ],
    instructions: [
      'Log into Shiprocket Dashboard > Settings > API > Webhooks.',
      `Add Webhook URL: ${API_BASE_URL}/api/webhooks/shiprocket`,
      'Alternative alias supported: /api/webhooks/shiprite',
      'Select Events: Tracking, Order Status Update, AWB Assign.',
      'Save configuration.',
    ],
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp (Enotify / Meta) Delivery Webhook',
    provider: 'Enotify / Meta',
    serviceType: 'whatsapp',
    icon: <WhatsAppIcon sx={{ color: '#10B981', fontSize: 28 }} />,
    color: '#10B981',
    method: 'POST / GET',
    endpointPath: '/api/webhooks/notifications/whatsapp/delivery',
    fullUrl: `${API_BASE_URL}/api/webhooks/notifications/whatsapp/delivery`,
    authType: 'Signature & Verification Token (X-Enotify-Signature / X-Hub-Signature)',
    description: 'Receives delivery confirmations, read receipts, and failure reports for automated WhatsApp order and shipment notifications.',
    events: [
      'message.sent',
      'message.delivered',
      'message.read',
      'message.failed',
    ],
    instructions: [
      'Log into Enotify App or Meta WhatsApp Business Manager > Webhooks.',
      `Callback URL: ${API_BASE_URL}/api/webhooks/notifications/whatsapp/delivery`,
      `Verify Token URL: ${API_BASE_URL}/api/webhooks/notifications/whatsapp/verify`,
      'Subscribe to messages & message_template_status_update.',
    ],
  },
  {
    id: 'email',
    title: 'SendGrid Email Delivery Webhook',
    provider: 'SendGrid',
    serviceType: 'email',
    icon: <EmailIcon sx={{ color: '#3B82F6', fontSize: 28 }} />,
    color: '#3B82F6',
    method: 'POST',
    endpointPath: '/api/webhooks/notifications/sendgrid',
    fullUrl: `${API_BASE_URL}/api/webhooks/notifications/sendgrid`,
    authType: 'SendGrid Event Webhook Signature Verification',
    description: 'Receives email delivery confirmations, opens, clicks, bounces, and drop events for transaction emails.',
    events: [
      'delivered',
      'processed',
      'bounce',
      'dropped',
      'deferred',
    ],
    instructions: [
      'Log into SendGrid Dashboard > Settings > Mail Settings > Event Webhook.',
      `HTTP Post URL: ${API_BASE_URL}/api/webhooks/notifications/sendgrid`,
      'Select actions: Dropped, Bounced, Delivered, Processed.',
      'Enable Event Webhook Status.',
    ],
  },
]

const IntegrationConfigPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Main Tab state synced with URL query (?tab=payment / logistics / whatsapp / sms / email / ai / webhooks / logs)
  const currentTabParam = searchParams.get('tab')?.toLowerCase() || 'payment'
  const activeTabIndex = TAB_KEYS.indexOf(currentTabParam) !== -1 ? TAB_KEYS.indexOf(currentTabParam) : 0

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    const tabName = TAB_KEYS[newIndex] || 'payment'
    setSearchParams({ tab: tabName })
  }

  // WhatsApp Sub-Tabs (0: Overview & Credentials, 1: Notification Settings, 2: Templates & Validation, 3: Test Messages)
  const [whatsappSubTab, setWhatsappSubTab] = useState(0)

  // Email Sub-Tabs (0: Overview & Credentials, 1: Notification Settings, 2: Templates & Preview, 3: Test Messages)
  const [emailSubTab, setEmailSubTab] = useState(0)

  // Integrations state
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')

  // Dialogs & Modals
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationConfig | null>(null)
  
  // Quick Edit / Create Dialog
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<IntegrationConfig | null>(null)
  const [selectedServiceTypeForCreate, setSelectedServiceTypeForCreate] = useState<ServiceType>('payment')
  const [selectedProviderForCreate, setSelectedProviderForCreate] = useState<string>('razorpay')

  // Test Connection Modal
  const [testDialogOpen, setTestDialogOpen] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null)

  // Webhook Instruction Modal
  const [instructionModalOpen, setInstructionModalOpen] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEndpointInfo | null>(null)

  // Webhook Logs State
  const [webhookLogs, setWebhookLogs] = useState<WebhookEventItem[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsTotal, setLogsTotal] = useState(0)
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsProviderFilter, setLogsProviderFilter] = useState<string>('all')
  const [logsStatusFilter, setLogsStatusFilter] = useState<string>('all')

  // Webhook Payload Viewer Modal
  const [payloadModalOpen, setPayloadModalOpen] = useState(false)
  const [selectedLogItem, setSelectedLogItem] = useState<WebhookEventItem | null>(null)

  // Snackbar for copy feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState('')
  const [syncingDefaults, setSyncingDefaults] = useState(false)

  const handleSyncDefaults = async () => {
    try {
      setSyncingDefaults(true)
      const response = await api.post('/admin/integrations/configs/sync-defaults')
      setSuccess(response.data?.message || 'Default integration configurations synchronized successfully!')
      await loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail?.message || err.message || 'Failed to sync default integration configs')
    } finally {
      setSyncingDefaults(false)
    }
  }

  useEffect(() => {
    loadIntegrations()
  }, [])

  useEffect(() => {
    if (activeTabIndex === 7) {
      loadWebhookLogs()
    }
  }, [activeTabIndex, logsPage, logsRowsPerPage, logsProviderFilter, logsStatusFilter])

  const loadIntegrations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/admin/integrations/configs', {
        params: { active_only: false },
      })
      const raw = response.data
      const list: IntegrationConfig[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.configs)
          ? raw.configs
          : Array.isArray(raw?.items)
            ? raw.items
            : Array.isArray(raw?.data)
              ? raw.data
              : []
      setIntegrations(list)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load integration configurations')
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }

  const loadWebhookLogs = async () => {
    try {
      setLogsLoading(true)
      const params: Record<string, any> = {
        limit: logsRowsPerPage,
        offset: logsPage * logsRowsPerPage,
      }
      if (logsProviderFilter !== 'all') {
        params.provider_name = logsProviderFilter
      }
      if (logsStatusFilter !== 'all') {
        params.status = logsStatusFilter
      }

      const response = await api.get('/admin/integrations/webhooks/events', { params })
      setWebhookLogs(response.data?.events || [])
      setLogsTotal(response.data?.total || 0)
    } catch (err: any) {
      console.error('Failed to load webhook logs:', err)
    } finally {
      setLogsLoading(false)
    }
  }

  const handleToggleActive = async (integration: IntegrationConfig) => {
    try {
      const newStatus = !integration.is_active
      await api.put(`/admin/integrations/configs/${integration.id}`, {
        is_active: newStatus,
      })
      setSuccess(`Integration ${integration.provider_name} is now ${newStatus ? 'Active' : 'Inactive'}`)
      loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update status')
    }
  }

  const handleSetPrimary = async (integration: IntegrationConfig) => {
    try {
      await api.put(`/admin/integrations/configs/${integration.id}`, {
        is_primary: true,
      })
      setSuccess(`${integration.provider_name} set as primary ${integration.service_type} provider`)
      loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to set as primary')
    }
  }

  const handleOpenEdit = (integration: IntegrationConfig) => {
    setEditingConfig(integration)
    setSelectedServiceTypeForCreate(integration.service_type)
    setSelectedProviderForCreate(integration.provider_name)
    setEditModalOpen(true)
  }

  const handleOpenCreateForTab = (serviceType: ServiceType) => {
    const defaultProviders: Record<ServiceType, string> = {
      payment: 'razorpay',
      logistics: 'shiprocket',
      whatsapp: 'enotify',
      sms: 'truebulksms',
      email: 'sendgrid',
      ai: 'sarvam_ai',
    }
    setEditingConfig(null)
    setSelectedServiceTypeForCreate(serviceType)
    setSelectedProviderForCreate(defaultProviders[serviceType] || 'custom')
    setEditModalOpen(true)
  }

  const handleSaveConfig = async (formData: any) => {
    try {
      if (editingConfig) {
        await api.put(`/admin/integrations/configs/${editingConfig.id}`, formData)
        setSuccess(`Configuration for ${editingConfig.provider_name} updated successfully in database!`)
      } else {
        await api.post('/admin/integrations/configs', {
          service_type: selectedServiceTypeForCreate,
          provider_name: selectedProviderForCreate,
          ...formData,
        })
        setSuccess(`New ${selectedServiceTypeForCreate} integration created successfully!`)
      }
      setEditModalOpen(false)
      loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail?.message || err.message || 'Failed to save configuration')
    }
  }

  const handleDelete = async () => {
    if (!selectedIntegration) return
    try {
      await api.delete(`/admin/integrations/configs/${selectedIntegration.id}`)
      setSuccess('Integration configuration deleted')
      setDeleteDialogOpen(false)
      loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete integration')
    }
  }

  const handleTestConnection = async (integration: IntegrationConfig) => {
    setSelectedIntegration(integration)
    setTestDialogOpen(true)
    setTestLoading(true)
    setTestResult(null)

    try {
      const response = await api.post(`/admin/integrations/configs/${integration.id}/test`)
      setTestResult({
        success: response.data?.success ?? true,
        message: response.data?.message || response.data?.details?.message || 'Connection established successfully!',
        details: response.data,
      })
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || err.response?.data?.detail?.message || err.message || 'Connection test failed',
        details: err.response?.data,
      })
    } finally {
      setTestLoading(false)
    }
  }

  const handleRetryWebhook = async (eventId: string) => {
    try {
      const response = await api.post(`/admin/integrations/webhooks/events/${eventId}/retry`)
      setSuccess(response.data?.message || 'Webhook reprocessed successfully')
      loadWebhookLogs()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to retry webhook')
    }
  }

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setSnackbarMsg(`Copied ${label} to clipboard!`)
    setSnackbarOpen(true)
  }

  // Filter integrations for active service tab
  const currentServiceKey = TAB_KEYS[activeTabIndex] as ServiceType
  const currentTabIntegrations = useMemo(() => {
    const list = Array.isArray(integrations) ? integrations : []
    return list.filter((item) => {
      const matchesType = item?.service_type?.toLowerCase() === currentServiceKey
      const matchesSearch =
        searchTerm === '' ||
        (item?.provider_name && item.provider_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item?.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      return matchesType && matchesSearch
    })
  }, [integrations, currentServiceKey, searchTerm])

  const getStatusChip = (status: string) => {
    const s = status?.toLowerCase()
    if (s === 'active' || s === 'success' || s === 'processed') {
      return <Chip icon={<CheckCircleIcon />} label="Active" color="success" size="small" variant="filled" />
    }
    if (s === 'error' || s === 'failed') {
      return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" variant="filled" />
    }
    if (s === 'maintenance') {
      return <Chip icon={<WarningIcon />} label="Maintenance" color="warning" size="small" variant="filled" />
    }
    return <Chip label={status || 'Inactive'} color="default" size="small" variant="outlined" />
  }

  // Helper to render the provider cards grid
  const renderProviderCards = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ color: '#0284C7' }} />
        </Box>
      )
    }

    if (currentTabIntegrations.length === 0) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed #CBD5E1',
            bgcolor: '#F8FAFC',
          }}
        >
          <InfoIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
            No {SERVICE_META[currentServiceKey]?.title || 'Integration'} Configured
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 450, mx: 'auto', mt: 0.5, mb: 2.5 }}>
            Click below to add custom credentials or sync all default integrations into the database.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCreateForTab(currentServiceKey)}
              sx={{ bgcolor: '#0284C7', textTransform: 'none', fontWeight: 600 }}
            >
              Add {SERVICE_META[currentServiceKey]?.title || 'Provider'}
            </Button>
            <Button
              variant="outlined"
              startIcon={syncingDefaults ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleSyncDefaults}
              disabled={syncingDefaults}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {syncingDefaults ? 'Syncing...' : 'Sync Default Integrations'}
            </Button>
          </Stack>
        </Paper>
      )
    }

    return (
      <Grid container spacing={3}>
        {currentTabIntegrations.map((item) => {
          const configData = item.config || item.config_data || {}
          const isPrimary = item.is_primary

          return (
            <Grid item xs={12} md={6} lg={6} key={item.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: isPrimary ? '2px solid #0284C7' : '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                  transition: 'all 0.2s ease',
                  boxShadow: isPrimary ? '0 4px 18px rgba(2, 132, 199, 0.12)' : '0 2px 8px rgba(0,0,0,0.03)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.07)',
                    borderColor: isPrimary ? '#0284C7' : '#CBD5E1',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header Row */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: SERVICE_META[item.service_type]?.bgColor || '#F1F5F9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {SERVICE_META[item.service_type]?.icon || <SettingsIcon />}
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', textTransform: 'capitalize' }}>
                            {item.provider_name.replace(/_/g, ' ')}
                          </Typography>
                          {isPrimary && (
                            <Chip
                              label="PRIMARY"
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          Priority: #{item.priority} | Service: {item.service_type.toUpperCase()}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {getStatusChip(item.health_status)}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.is_active}
                            onChange={() => handleToggleActive(item)}
                            color="success"
                            size="small"
                          />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>{item.is_active ? 'Active' : 'Disabled'}</Typography>}
                        sx={{ mr: 0 }}
                      />
                    </Stack>
                  </Stack>

                  <Typography variant="body2" sx={{ color: '#475569', mb: 2.5, minHeight: 40, fontSize: '0.88rem' }}>
                    {item.description || 'Configured via database integration manager.'}
                  </Typography>

                  <Divider sx={{ my: 1.5, borderColor: '#F1F5F9' }} />

                  {/* Credential Attributes */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Stored Configuration Attributes (Encrypted)
                  </Typography>

                  <Paper elevation={0} sx={{ p: 1.5, mt: 1, mb: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #EEF2F6' }}>
                    <Grid container spacing={1}>
                      {Object.entries(configData).map(([key, val]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, minWidth: 90 }}>
                              {key}:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#1E293B',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {typeof val === 'boolean' ? (val ? 'true' : 'false') : String(val || '—')}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  {/* Stats counters */}
                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, bgcolor: '#F1F5F9', borderRadius: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Total Calls</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                          {item.total_requests || 0}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, bgcolor: 'rgba(22, 163, 74, 0.08)', borderRadius: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#16A34A', display: 'block' }}>Successful</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                          {item.successful_requests || 0}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, bgcolor: 'rgba(225, 29, 72, 0.08)', borderRadius: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#E11D48', display: 'block' }}>Failed</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#E11D48' }}>
                          {item.failed_requests || 0}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>

                <Divider sx={{ borderColor: '#F1F5F9' }} />

                {/* Card Actions */}
                <CardActions sx={{ p: 2, px: 3, justifyContent: 'space-between', bgcolor: '#FAFAFC' }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TestIcon />}
                      onClick={() => handleTestConnection(item)}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Test Live
                    </Button>
                    {!isPrimary && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<StarBorderIcon />}
                        onClick={() => handleSetPrimary(item)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}
                      >
                        Make Primary
                      </Button>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedIntegration(item)
                        setDeleteDialogOpen(true)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1600, mx: 'auto' }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <SettingsIcon sx={{ fontSize: 32, color: '#38BDF8' }} />
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#F8FAFC' }}>
                  Integrations & API Services Hub
                </Typography>
                <Chip
                  label="Database Synced"
                  size="small"
                  icon={<ShieldIcon sx={{ fontSize: '14px !important', color: '#10B981 !important' }} />}
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.15)',
                    color: '#34D399',
                    fontWeight: 600,
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                />
              </Stack>
              <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: 850, fontSize: '0.95rem' }}>
                Unified control panel for all external service providers. Credentials are securely encrypted in PostgreSQL database (no static .env fallbacks required).
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={syncingDefaults ? <CircularProgress size={16} sx={{ color: '#38BDF8' }} /> : <RefreshIcon />}
                onClick={handleSyncDefaults}
                disabled={syncingDefaults}
                sx={{
                  color: '#F1F5F9',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#38BDF8', bgcolor: 'rgba(56, 189, 248, 0.08)' },
                }}
              >
                {syncingDefaults ? 'Syncing...' : 'Sync Defaults'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  loadIntegrations()
                  if (activeTabIndex === 7) loadWebhookLogs()
                  setSuccess('Integration statuses refreshed')
                }}
                sx={{
                  color: '#F1F5F9',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#38BDF8', bgcolor: 'rgba(56, 189, 248, 0.08)' },
                }}
              >
                Refresh
              </Button>
              {activeTabIndex < 6 && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenCreateForTab(currentServiceKey)}
                  sx={{
                    bgcolor: '#0284C7',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    boxShadow: '0 4px 14px 0 rgba(2, 132, 199, 0.4)',
                    '&:hover': { bgcolor: '#0369A1' },
                  }}
                >
                  Add Configuration
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Notifications */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2.5, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Main Navigation Tabs Bar */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          p: 0.5,
          boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        }}
      >
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 52,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 2,
              backgroundColor: '#0284C7',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.92rem',
              minHeight: 50,
              px: 2.5,
              color: '#64748B',
              '&.Mui-selected': {
                color: '#0284C7',
                fontWeight: 700,
              },
            },
          }}
        >
          <Tab icon={<PaymentIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Payment Gateway" />
          <Tab icon={<ShippingIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Logistics & Shipping" />
          <Tab icon={<WhatsAppIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="WhatsApp Services" />
          <Tab icon={<SmsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="SMS & OTP Gateway" />
          <Tab icon={<EmailIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Email Services" />
          <Tab icon={<AiIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="AI Engine" />
          <Tab icon={<WebhookIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Webhooks Hub" />
          <Tab icon={<LogsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Audit & Logs" />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* TAB 0: PAYMENT GATEWAY */}
      {/* ========================================================================= */}
      {activeTabIndex === 0 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: SERVICE_META.payment.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', display: 'flex' }}>
                {SERVICE_META.payment.icon}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem' }}>
                  {SERVICE_META.payment.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.2 }}>
                  {SERVICE_META.payment.description}
                </Typography>
              </Box>
            </Stack>

            <TextField
              size="small"
              placeholder="Search Payment Providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#FFFFFF', borderRadius: 2, minWidth: 260 }}
            />
          </Paper>

          {renderProviderCards()}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 1: LOGISTICS & SHIPPING */}
      {/* ========================================================================= */}
      {activeTabIndex === 1 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: SERVICE_META.logistics.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', display: 'flex' }}>
                {SERVICE_META.logistics.icon}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem' }}>
                  {SERVICE_META.logistics.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.2 }}>
                  {SERVICE_META.logistics.description}
                </Typography>
              </Box>
            </Stack>

            <TextField
              size="small"
              placeholder="Search Logistics Carriers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#FFFFFF', borderRadius: 2, minWidth: 260 }}
            />
          </Paper>

          {renderProviderCards()}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: WHATSAPP SERVICES (With Embedded Notification Settings, Templates & Test) */}
      {/* ========================================================================= */}
      {activeTabIndex === 2 && (
        <Box>
          {/* WhatsApp Sub-Tabs Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(22, 163, 74, 0.08)', display: 'flex' }}>
                <WhatsAppIcon sx={{ color: '#16A34A', fontSize: 24 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                WhatsApp Cloud Center
              </Typography>
            </Stack>

            <ToggleButtonGroup
              value={whatsappSubTab}
              exclusive
              onChange={(_, next) => next !== null && setWhatsappSubTab(next)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  px: 2,
                  py: 0.8,
                  borderColor: '#E2E8F0',
                  color: '#64748B',
                  '&.Mui-selected': {
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#15803D' },
                  },
                },
              }}
            >
              <ToggleButton value={0}>
                <SettingsIcon sx={{ fontSize: 16, mr: 0.8 }} /> Providers & Credentials
              </ToggleButton>
              <ToggleButton value={1}>
                <TriggerIcon sx={{ fontSize: 16, mr: 0.8 }} /> Notification Settings
              </ToggleButton>
              <ToggleButton value={2}>
                <TemplateIcon sx={{ fontSize: 16, mr: 0.8 }} /> Templates & Validation
              </ToggleButton>
              <ToggleButton value={3}>
                <SendIcon sx={{ fontSize: 16, mr: 0.8 }} /> Send Test Message
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* WhatsApp Sub Tab Content */}
          {whatsappSubTab === 0 && renderProviderCards()}
          {whatsappSubTab === 1 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <WhatsAppNotificationSettings />
            </Paper>
          )}
          {whatsappSubTab === 2 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <WhatsAppTemplatesTab />
            </Paper>
          )}
          {whatsappSubTab === 3 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <WhatsAppTestMessage />
            </Paper>
          )}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: SMS & OTP GATEWAY */}
      {/* ========================================================================= */}
      {activeTabIndex === 3 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: SERVICE_META.sms.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', display: 'flex' }}>
                {SERVICE_META.sms.icon}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem' }}>
                  {SERVICE_META.sms.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.2 }}>
                  {SERVICE_META.sms.description}
                </Typography>
              </Box>
            </Stack>

            <TextField
              size="small"
              placeholder="Search SMS Gateways..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#FFFFFF', borderRadius: 2, minWidth: 260 }}
            />
          </Paper>

          {renderProviderCards()}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: EMAIL SERVICES (With Embedded Notification Settings, Templates & Test) */}
      {/* ========================================================================= */}
      {activeTabIndex === 4 && (
        <Box>
          {/* Email Sub-Tabs Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(37, 99, 235, 0.08)', display: 'flex' }}>
                <EmailIcon sx={{ color: '#2563EB', fontSize: 24 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                Email Delivery Center
              </Typography>
            </Stack>

            <ToggleButtonGroup
              value={emailSubTab}
              exclusive
              onChange={(_, next) => next !== null && setEmailSubTab(next)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  px: 2,
                  py: 0.8,
                  borderColor: '#E2E8F0',
                  color: '#64748B',
                  '&.Mui-selected': {
                    bgcolor: '#2563EB',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#1D4ED8' },
                  },
                },
              }}
            >
              <ToggleButton value={0}>
                <SettingsIcon sx={{ fontSize: 16, mr: 0.8 }} /> Providers & Credentials
              </ToggleButton>
              <ToggleButton value={1}>
                <TriggerIcon sx={{ fontSize: 16, mr: 0.8 }} /> Notification Settings
              </ToggleButton>
              <ToggleButton value={2}>
                <TemplateIcon sx={{ fontSize: 16, mr: 0.8 }} /> Templates & Preview
              </ToggleButton>
              <ToggleButton value={3}>
                <SendIcon sx={{ fontSize: 16, mr: 0.8 }} /> Send Test Email
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* Email Sub Tab Content */}
          {emailSubTab === 0 && renderProviderCards()}
          {emailSubTab === 1 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <EmailNotificationSettingsTab />
            </Paper>
          )}
          {emailSubTab === 2 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <EmailTemplatesTab />
            </Paper>
          )}
          {emailSubTab === 3 && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <EmailTestMessageTab />
            </Paper>
          )}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 5: AI ENGINE */}
      {/* ========================================================================= */}
      {activeTabIndex === 5 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: SERVICE_META.ai.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', display: 'flex' }}>
                {SERVICE_META.ai.icon}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem' }}>
                  {SERVICE_META.ai.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.2 }}>
                  {SERVICE_META.ai.description}
                </Typography>
              </Box>
            </Stack>

            <TextField
              size="small"
              placeholder="Search AI Models..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#FFFFFF', borderRadius: 2, minWidth: 260 }}
            />
          </Paper>

          {renderProviderCards()}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 6: WEBHOOKS HUB (Live Endpoints, Authentication, Setup Guides) */}
      {/* ========================================================================= */}
      {activeTabIndex === 6 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3.5,
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: 'rgba(124, 58, 237, 0.04)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                <WebhookIcon sx={{ fontSize: 32, color: '#7C3AED' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>
                  Live Webhook Endpoints & Push Dispatchers
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mt: 0.3 }}>
                  Provide these exact URLs in your vendor developer dashboards (Razorpay, Delhivery, Shiprocket, eNotify, SendGrid) to receive automated real-time status updates and order synchronization.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={3}>
            {WEBHOOK_ENDPOINTS.map((endpoint) => (
              <Grid item xs={12} md={6} key={endpoint.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                      borderColor: '#CBD5E1',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#F8FAFC', display: 'flex' }}>
                        {endpoint.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                          {endpoint.title}
                        </Typography>
                        <Chip
                          label={endpoint.method}
                          size="small"
                          sx={{
                            bgcolor: '#EEF2FF',
                            color: '#4F46E5',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 18,
                          }}
                        />
                      </Box>
                    </Stack>

                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.86rem', mb: 2 }}>
                      {endpoint.description}
                    </Typography>

                    {/* Copyable Webhook URL Box */}
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', display: 'block', mb: 0.5 }}>
                      PUBLIC WEBHOOK URL
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: '#0F172A',
                        color: '#38BDF8',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontFamily: 'monospace',
                        fontSize: '0.82rem',
                        wordBreak: 'break-all',
                        mb: 2,
                      }}
                    >
                      <span>{endpoint.fullUrl}</span>
                      <Tooltip title="Copy Webhook URL">
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(endpoint.fullUrl, endpoint.title)}
                          sx={{ color: '#94A3B8', ml: 1, '&:hover': { color: '#FFFFFF' } }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>

                    {/* Verification Method */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <ShieldIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>
                        Verification: {endpoint.authType}
                      </Typography>
                    </Stack>

                    {/* Subscribed Events */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', display: 'block', mb: 0.5 }}>
                        SUPPORTED EVENT TRIGGERS
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.6}>
                        {endpoint.events.map((ev) => (
                          <Chip
                            key={ev}
                            label={ev}
                            size="small"
                            sx={{
                              bgcolor: '#F1F5F9',
                              color: '#334155',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              height: 22,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>

                  <Divider sx={{ borderColor: '#F1F5F9' }} />

                  <CardActions sx={{ p: 2, px: 3, bgcolor: '#FAFAFC', justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<HelpIcon />}
                      onClick={() => {
                        setSelectedWebhook(endpoint)
                        setInstructionModalOpen(true)
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600, color: '#0284C7' }}
                    >
                      Step-by-Step Setup Guide
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LogsIcon />}
                      onClick={() => {
                        setLogsProviderFilter(endpoint.provider.toLowerCase().replace(/\s.*/, ''))
                        setSearchParams({ tab: 'logs' })
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                    >
                      View Live Logs
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 7: AUDIT & WEBHOOK LOGS */}
      {/* ========================================================================= */}
      {activeTabIndex === 7 && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <LogsIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                Incoming Webhook Events & Audit Log
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                select
                size="small"
                label="Provider"
                value={logsProviderFilter}
                onChange={(e) => {
                  setLogsProviderFilter(e.target.value)
                  setLogsPage(0)
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Providers</MenuItem>
                <MenuItem value="razorpay">Razorpay</MenuItem>
                <MenuItem value="delhivery">Delhivery</MenuItem>
                <MenuItem value="shiprocket">Shiprocket</MenuItem>
                <MenuItem value="enotify">eNotify WhatsApp</MenuItem>
                <MenuItem value="sendgrid">SendGrid</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Status"
                value={logsStatusFilter}
                onChange={(e) => {
                  setLogsStatusFilter(e.target.value)
                  setLogsPage(0)
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadWebhookLogs}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Refresh Logs
              </Button>
            </Stack>
          </Paper>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Event Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Signature</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Order / Reference</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : webhookLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748B' }}>
                      No webhook events logged yet. Trigger an action or configure your vendor webhook URL.
                    </TableCell>
                  </TableRow>
                ) : (
                  webhookLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ fontSize: '0.85rem', color: '#64748B' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, textTransform: 'capitalize', color: '#0F172A' }}>
                        {log.provider_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.event_type}
                          size="small"
                          sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        {log.signature_verified ? (
                          <Chip icon={<CheckCircleIcon />} label="Verified" color="success" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Unverified / IP" color="default" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(log.processing_status)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                        {log.order_id || log.event_id || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Payload">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedLogItem(log)
                                setPayloadModalOpen(true)
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {log.processing_status === 'failed' && (
                            <Tooltip title="Retry Webhook Processing">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleRetryWebhook(log.id)}
                              >
                                <RetryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={logsTotal}
              page={logsPage}
              onPageChange={(_, newPage) => setLogsPage(newPage)}
              rowsPerPage={logsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setLogsRowsPerPage(parseInt(e.target.value, 10))
                setLogsPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </TableContainer>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* MODALS & DIALOGS */}
      {/* ========================================================================= */}

      {/* Quick Edit / Add Configuration Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A', pb: 1 }}>
          {editingConfig ? `Edit ${editingConfig.provider_name.replace(/_/g, ' ')} Configuration` : `New ${selectedServiceTypeForCreate.toUpperCase()} Configuration`}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <IntegrationConfigForm
            serviceType={editingConfig ? editingConfig.service_type : selectedServiceTypeForCreate}
            providerName={editingConfig ? editingConfig.provider_name : selectedProviderForCreate}
            initialData={editingConfig ? {
              is_active: editingConfig.is_active,
              is_primary: editingConfig.is_primary,
              priority: editingConfig.priority,
              description: editingConfig.description,
              config: editingConfig.config || editingConfig.config_data || {},
            } : undefined}
            integrationId={editingConfig?.id}
            onSubmit={handleSaveConfig}
            onCancel={() => setEditModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Live Test Diagnostic Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A' }}>
          Live Connection Test
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {testLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={44} sx={{ color: '#0284C7', mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
                Connecting to {selectedIntegration?.provider_name}...
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Verifying API credentials, SSL handshake, and remote endpoint responsiveness.
              </Typography>
            </Box>
          ) : testResult ? (
            <Box>
              <Alert
                severity={testResult.success ? 'success' : 'error'}
                icon={testResult.success ? <CheckCircleIcon fontSize="inherit" /> : <ErrorIcon fontSize="inherit" />}
                sx={{ mb: 2.5, borderRadius: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {testResult.success ? 'Connection Succeeded' : 'Connection Failed'}
                </Typography>
                <Typography variant="body2">{testResult.message}</Typography>
              </Alert>

              {testResult.details && (
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1 }}>
                    DIAGNOSTIC DETAILS
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#0F172A',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(testResult.details, null, 2)}
                  </Box>
                </Paper>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={() => setTestDialogOpen(false)} sx={{ fontWeight: 600 }}>
            Close
          </Button>
          {selectedIntegration && (
            <Button
              variant="contained"
              onClick={() => handleTestConnection(selectedIntegration)}
              disabled={testLoading}
              sx={{ bgcolor: '#0284C7', fontWeight: 600 }}
            >
              Re-test Connection
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Webhook Step-by-Step Setup Guide Dialog */}
      <Dialog
        open={instructionModalOpen}
        onClose={() => setInstructionModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A' }}>
          {selectedWebhook?.title} - Setup Instructions
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedWebhook && (
            <Box>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#0F172A', color: '#38BDF8', borderRadius: 2, mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 0.5 }}>
                  TARGET WEBHOOK URL (COPY THIS)
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {selectedWebhook.fullUrl}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(selectedWebhook.fullUrl, 'Webhook URL')}
                    sx={{ bgcolor: '#0284C7', textTransform: 'none', ml: 2 }}
                  >
                    Copy
                  </Button>
                </Stack>
              </Paper>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5 }}>
                Integration Steps:
              </Typography>

              <Stack spacing={1.5}>
                {selectedWebhook.instructions.map((step, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{ p: 2, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: '#0284C7',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#334155', pt: 0.3, fontWeight: 500 }}>
                      {step}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={() => setInstructionModalOpen(false)} variant="contained" sx={{ bgcolor: '#0284C7' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Webhook Payload Viewer Modal */}
      <Dialog
        open={payloadModalOpen}
        onClose={() => setPayloadModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A' }}>
          Webhook Payload Details
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedLogItem && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`Provider: ${selectedLogItem.provider_name}`} size="small" />
                <Chip label={`Event: ${selectedLogItem.event_type}`} size="small" color="primary" />
                {getStatusChip(selectedLogItem.processing_status)}
              </Stack>

              {selectedLogItem.processing_error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {selectedLogItem.processing_error}
                </Alert>
              )}

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', display: 'block', mb: 0.5 }}>
                PARSED JSON PAYLOAD
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#0F172A',
                  color: '#34D399',
                  borderRadius: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(selectedLogItem.parsed_payload || selectedLogItem.raw_payload, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={() => setPayloadModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A' }}>Delete Configuration?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Are you sure you want to delete the configuration for{' '}
            <strong>{selectedIntegration?.provider_name}</strong>? This will remove credentials from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
      />
    </Box>
  )
}

export default IntegrationConfigPage
